import React, { useEffect, useState } from "react";
import {
    BookOpen,
    Plus,
    Pencil,
    Trash2,
    Loader2,
    X,
    Save,
    Eye,
    ExternalLink,
    Globe,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import RichTextEditor from "../../components/admin/RichTextEditor";

const STATUS = {
    draft: { label: "Taslak", color: "bg-slate-100 text-slate-600" },
    published: { label: "Yayında", color: "bg-data-green/10 text-data-green" },
    archived: { label: "Arşiv", color: "bg-amber-100 text-amber-700" },
};

const LANGS = ["tr", "en", "de"];

export default function AdminBlogPosts() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [meta, setMeta] = useState({ page: 1, total: 0, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState(null);

    const load = async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), page_size: "20" });
            if (filter) params.set("status", filter);
            const [{ data }, { data: cats }] = await Promise.all([
                api.get(`/admin/content/blog-posts?${params}`),
                api.get("/admin/content/blog-categories"),
            ]);
            setItems(data.items || []);
            setMeta({ page: data.page, total: data.total, pages: data.pages });
            setCategories(cats.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(1); }, [filter]);

    const create = () => setEditing({
        title: { tr: "", en: "", de: "" },
        excerpt: { tr: "", en: "", de: "" },
        content: { tr: "<p></p>", en: "", de: "" },
        category_id: categories[0]?.id || "",
        tags: [],
        featured_image: "",
        author: { name: "Otel Pro Editör" },
        seo: { meta_title: "", meta_desc: "" },
        status: "draft",
        reading_time: 4,
        isNew: true,
    });

    const editItem = async (id) => {
        try {
            const { data } = await api.get(`/admin/content/blog-posts/${id}`);
            setEditing({ ...data, isNew: false });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const save = async () => {
        if (!editing.title?.tr) {
            toast.error("Başlık (TR) zorunlu.");
            return;
        }
        try {
            const payload = { ...editing };
            delete payload.isNew;
            if (!editing.isNew) payload.id = editing.id;
            const { data } = await api.post("/admin/content/blog-posts", payload);
            toast.success(`Kaydedildi: /blog/${data.slug}`);
            setEditing(null);
            await load(meta.page);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Yazı silinecek. Onaylıyor musunuz?")) return;
        try {
            await api.delete(`/admin/content/blog-posts/${id}`);
            toast.success("Silindi.");
            await load(meta.page);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-blog-posts-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <BookOpen className="w-6 h-6 text-teal" /> Blog Yazıları
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        TipTap zengin metin editörü ile çoklu dilli (TR/EN/DE) yayın yönetimi. AI üretimi taslakları da burada düzenleyebilirsiniz.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                        data-testid="blog-posts-filter"
                    >
                        <option value="">Tümü</option>
                        <option value="draft">Taslak</option>
                        <option value="published">Yayında</option>
                        <option value="archived">Arşiv</option>
                    </select>
                    <button
                        onClick={create}
                        className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                        data-testid="blog-posts-new"
                    >
                        <Plus className="w-4 h-4" /> Yeni Yazı
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Henüz yazı yok.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="blog-posts-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Başlık (TR)</th>
                                <th className="px-4 py-3">Kategori</th>
                                <th className="px-4 py-3">Durum</th>
                                <th className="px-4 py-3">Tarih</th>
                                <th className="px-4 py-3 w-44">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => {
                                const st = STATUS[p.status] || { label: p.status, color: "bg-slate-100 text-slate-600" };
                                const cat = categories.find((c) => c.id === p.category_id);
                                return (
                                    <tr key={p.id} className="border-t border-slate-100">
                                        <td className="px-4 py-3 max-w-md">
                                            <div className="font-semibold text-dark-navy text-sm">{p.title?.tr || "—"}</div>
                                            <div className="text-xs text-deep-blue/60">/{p.slug}</div>
                                            {p.ai_generated && (
                                                <span className="inline-block mt-1 text-[10px] bg-teal/10 text-teal-700 px-1.5 py-0.5 rounded">AI ÜRETİMİ</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-deep-blue/80">{cat?.name?.tr || "—"}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${st.color}`}>{st.label}</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-deep-blue/70 font-data">
                                            {p.published_at ? new Date(p.published_at).toLocaleDateString("tr-TR") : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                {p.status === "published" && (
                                                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="Yayınlanmışı Aç" className="p-1.5 rounded-md hover:bg-light-bg">
                                                        <ExternalLink className="w-4 h-4 text-teal-700" />
                                                    </a>
                                                )}
                                                <button onClick={() => editItem(p.id)} title="Düzenle" className="p-1.5 rounded-md hover:bg-light-bg" data-testid={`blog-post-edit-${p.id}`}>
                                                    <Pencil className="w-4 h-4 text-deep-blue" />
                                                </button>
                                                <button onClick={() => remove(p.id)} title="Sil" className="p-1.5 rounded-md hover:bg-light-bg">
                                                    <Trash2 className="w-4 h-4 text-data-red" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {meta.pages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                    <button onClick={() => load(meta.page - 1)} disabled={meta.page <= 1} className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-sm disabled:opacity-50">‹</button>
                    <span className="text-sm font-semibold text-deep-blue">{meta.page} / {meta.pages}</span>
                    <button onClick={() => load(meta.page + 1)} disabled={meta.page >= meta.pages} className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-sm disabled:opacity-50">›</button>
                </div>
            )}

            {editing && (
                <BlogPostEditor
                    post={editing}
                    setPost={setEditing}
                    categories={categories}
                    onSave={save}
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
}

function BlogPostEditor({ post, setPost, categories, onSave, onClose }) {
    const [activeLang, setActiveLang] = useState("tr");
    const update = (key, value) => setPost((p) => ({ ...p, [key]: value }));
    const updateLang = (key, lang, value) =>
        setPost((p) => ({ ...p, [key]: { ...(p[key] || {}), [lang]: value } }));

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="blog-post-editor">
            <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-4xl w-full my-8">
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <h2 className="text-base font-semibold text-dark-navy">{post.isNew ? "Yeni Blog Yazısı" : `Düzenle: ${post.title?.tr || ""}`}</h2>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-light-bg">
                        <X className="w-4 h-4 text-deep-blue" />
                    </button>
                </div>
                <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                    {/* Lang tabs */}
                    <div className="flex items-center gap-1 border-b border-slate-200">
                        {LANGS.map((l) => (
                            <button
                                key={l}
                                onClick={() => setActiveLang(l)}
                                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${activeLang === l ? "border-b-2 border-teal text-teal-700" : "text-deep-blue/70"}`}
                                data-testid={`blog-post-lang-${l}`}
                            >
                                <Globe className="w-3 h-3 inline mr-1" /> {l}
                            </button>
                        ))}
                    </div>

                    <input
                        placeholder={`Başlık (${activeLang})`}
                        value={post.title?.[activeLang] || ""}
                        onChange={(e) => updateLang("title", activeLang, e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-slate-300 text-base font-semibold"
                        data-testid={`blog-post-title-${activeLang}`}
                    />

                    <textarea
                        placeholder={`Özet (${activeLang})`}
                        rows={2}
                        value={post.excerpt?.[activeLang] || ""}
                        onChange={(e) => updateLang("excerpt", activeLang, e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                    />

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80 mb-1 block">İçerik ({activeLang})</label>
                        <RichTextEditor
                            key={`content-${activeLang}`}
                            value={post.content?.[activeLang] || ""}
                            onChange={(v) => updateLang("content", activeLang, v)}
                            testId={`blog-post-content-${activeLang}`}
                        />
                    </div>

                    {/* Common fields */}
                    <div className="grid sm:grid-cols-3 gap-3 pt-3 border-t border-slate-200">
                        <Field label="Kategori">
                            <select
                                value={post.category_id || ""}
                                onChange={(e) => update("category_id", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                                data-testid="blog-post-category"
                            >
                                <option value="">— Seçim —</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name?.tr}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Durum">
                            <select
                                value={post.status || "draft"}
                                onChange={(e) => update("status", e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                                data-testid="blog-post-status"
                            >
                                <option value="draft">Taslak</option>
                                <option value="published">Yayında</option>
                                <option value="archived">Arşiv</option>
                            </select>
                        </Field>
                        <Field label="Okuma Süresi (dk)">
                            <input
                                type="number"
                                value={post.reading_time || 4}
                                onChange={(e) => update("reading_time", parseInt(e.target.value, 10) || 4)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-data"
                            />
                        </Field>
                    </div>

                    <Field label="Etiketler (virgülle ayır)">
                        <input
                            value={(post.tags || []).join(", ")}
                            onChange={(e) => update("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                            placeholder="2026, otel, yatırım"
                        />
                    </Field>

                    <Field label="Öne Çıkan Görsel (URL)">
                        <input
                            value={post.featured_image || ""}
                            onChange={(e) => update("featured_image", e.target.value)}
                            placeholder="https://..."
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono"
                        />
                    </Field>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="SEO Meta Title">
                            <input
                                value={post.seo?.meta_title || ""}
                                onChange={(e) => update("seo", { ...(post.seo || {}), meta_title: e.target.value })}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                maxLength={60}
                            />
                        </Field>
                        <Field label="SEO Meta Description">
                            <input
                                value={post.seo?.meta_desc || ""}
                                onChange={(e) => update("seo", { ...(post.seo || {}), meta_desc: e.target.value })}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                maxLength={155}
                            />
                        </Field>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
                    <button onClick={onClose} className="px-4 py-2 rounded-md bg-light-bg text-deep-blue text-sm font-semibold">İptal</button>
                    <button
                        onClick={onSave}
                        className="px-4 py-2 rounded-md bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1"
                        data-testid="blog-post-save"
                    >
                        <Save className="w-4 h-4" /> Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
